/* MolCellQuest — 進化ポイント(evo)の経済、ランク、実績
   ──────────────────────────────────────────────────────────
   要点：報酬は「現在の evo に対する割合」で決まる（幾何級数）。
   これにより 46億年前 → 現在 という6桁またぐ旅を、常に
   「1問で目に見えて進む」体感のまま通り抜けられる。
   ────────────────────────────────────────────────────────── */

import { progress, save, DAILY_GOAL } from "./state.svelte.js";
import { today } from "./format.js";
import { CURRICULUM } from "../../data/problems/index.js";
import { CATEGORIES, TRACKS } from "../../data/taxonomy.js";
import { eraForEvo } from "../../data/journeyEngine.js";
import { ERAS } from "../../data/eras.js";

/* ── 難易度 ───────────────────────────────────────────── */

export const DIFF_TIERS = [
  { min: 0, label: "基礎", cls: "easy", color: "#74c365" },
  { min: 180, label: "標準", cls: "mid", color: "#7bd8ff" },
  { min: 270, label: "応用", cls: "hard", color: "#ffa657" },
];

export function diffMeta(n) {
  let t = DIFF_TIERS[0];
  for (const x of DIFF_TIERS) if (n >= x.min) t = x;
  return t;
}

/* ── ランク ───────────────────────────────────────────── */

export const RANKS = [
  { min: 0, em: "🔬", name: "細胞の見習い", color: "#8fbf7a", pos: "上位100%", desc: "まずは顕微鏡を覗くところから。" },
  { min: 120, em: "🧫", name: "生化学徒", color: "#5fc9a8", pos: "上位75%", desc: "分子の名前と役割がつながってきた。" },
  { min: 200, em: "🧬", name: "分子生物学者", color: "#4aa8e0", pos: "上位50%", desc: "セントラルドグマを自在に行き来できる。" },
  { min: 280, em: "🔭", name: "細胞生物学の探究者", color: "#a586e8", pos: "上位25%", desc: "現象を機構で説明できるようになった。" },
  { min: 360, em: "📜", name: "ゲノムの解読者", color: "#e8a13c", pos: "上位10%", desc: "実験データから仮説を立てられる。" },
  { min: 450, em: "✨", name: "生命の設計者", color: "#ff6b6b", pos: "上位2%", desc: "読むだけでなく、書く側に回れる。" },
];

/** 実力レート = 正解した問題のうち、難易度が高い上位3問の平均 */
export function myRating() {
  const solvedDiffs = CURRICULUM.filter((p) => progress.solved[p.id])
    .map((p) => p.diff)
    .sort((a, b) => b - a)
    .slice(0, 3);
  if (solvedDiffs.length === 0) return 0;
  const sum = solvedDiffs.reduce((a, b) => a + b, 0);
  return Math.round(sum / solvedDiffs.length);
}

export function ratingInfo(rating) {
  let m = RANKS[0];
  for (const r of RANKS) if (rating >= r.min) m = r;
  return m;
}

export function rankIndex() {
  const r = myRating();
  let idx = 0;
  RANKS.forEach((rk, k) => {
    if (r >= rk.min) idx = k;
  });
  return idx;
}

/** 次のランクまでの進捗（0〜1）と残りレート */
export function rankProgress() {
  const rating = myRating();
  const cur = ratingInfo(rating);
  const i = RANKS.indexOf(cur);
  const next = RANKS[i + 1];
  if (!next) return { percent: 1, next: null, remaining: 0, cur };
  const percent = Math.min(1, Math.max(0, (rating - cur.min) / (next.min - cur.min)));
  return { percent, next, remaining: next.min - rating, cur };
}

/* ── 報酬 ─────────────────────────────────────────────── */

/* この4つの定数は tools/simulate.mjs にも写してある。
   変更したら両方を合わせ、`npm run simulate -- --fit` で
   data/eras.js の len を取り直すこと。 */
export const EVO_FLOOR = 0.026; // 最初の一歩の大きさ
export const EVO_GROWTH = 0.065; // 標準問題1問 = 現在の evo の 6.5%
export const RANK_SLOPE = 0.15; // ランクが1段上がるごとの上乗せ

export function baseStep() {
  return Math.max(EVO_FLOOR, progress.evo * EVO_GROWTH);
}

export function diffFactor(diff) {
  const l = diffMeta(diff).label;
  return l === "基礎" ? 0.6 : l === "標準" ? 1 : 1.8;
}

export function rankFactor() {
  return 1 + RANK_SLOPE * rankIndex();
}

/** 1問正解したときに得られる evo。復習（2回目以降）は30%。 */
export function solveEvo(diff, firstTime) {
  const g = baseStep() * diffFactor(diff) * rankFactor();
  return firstTime ? g : g * 0.3;
}

/** 未解答の問題を解いたときの獲得予定 evo（一覧表示用） */
export function firstSolveEvo(diff) {
  return solveEvo(diff, true);
}

export function addEvo(n) {
  progress.evo += Math.max(0, n || 0);
}

/**
 * 目標の evo に届くまで、標準問題をあと何問解けばよいかの目安。
 * 生の進化ポイント（「あと 0.3」）は学習者にとって意味を持たないので、
 * 画面ではこちらを見せる。報酬は幾何級数だが、序盤は下限値が効く定数域なので
 * 素直に1問ずつ回して数える（十分に速く、両方の領域を正しく扱える）。
 */
export function problemsToGo(targetEvo) {
  let evo = progress.evo;
  if (!isFinite(targetEvo) || targetEvo <= evo) return 0;
  const rf = rankFactor();
  let n = 0;
  while (evo < targetEvo && n < 999) {
    evo += Math.max(EVO_FLOOR, evo * EVO_GROWTH) * rf;
    n++;
  }
  return n;
}

/* ── コンボ・ストリーク・記録 ─────────────────────────── */

export function recordAttempt(probId, ok, ms) {
  /* ★ 代入式の値（素の配列）を掴んで push してはいけない。
     progress は $state プロキシなので、代入した配列は改めてプロキシに包まれて
     格納される。素の配列を直接いじると、プロキシが持つ length の追跡を
     すり抜けてしまい、追加した要素が読み出し（＝保存や画面表示）に出てこない。
     必ず progress 経由で取り直したものを操作する。 */
  if (!Array.isArray(progress.attempts[probId])) progress.attempts[probId] = [];
  const list = progress.attempts[probId];
  list.push({ t: Date.now(), ok, ms });
  if (list.length > 50) list.splice(0, list.length - 50);

  progress.solveTimeTotal += ms || 0;
  if (ok) {
    const best = progress.bestTimes[probId];
    if (best == null || ms < best) progress.bestTimes[probId] = ms;
  }
}

export function recordCorrect(probId) {
  const t = today();
  progress.activity[t] = (progress.activity[t] || 0) + 1;
  progress.combo += 1;
  if (progress.combo > progress.bestCombo) progress.bestCombo = progress.combo;
  progress.solved[probId] = true;
  updateStreak();
}

export function breakCombo() {
  progress.combo = 0;
}

export function updateStreak() {
  const t = today();
  if (progress.lastActive === t) return;
  if (progress.lastActive) {
    const diffDays = Math.round(
      Math.abs(new Date(t + "T00:00:00") - new Date(progress.lastActive + "T00:00:00")) / 86400000
    );
    if (diffDays === 1) progress.streak += 1;
    else if (diffDays > 1) progress.streak = 1;
  } else {
    progress.streak = 1;
  }
  progress.lastActive = t;
  if (progress.streak > (progress.bestStreak || 0)) progress.bestStreak = progress.streak;
}

export function todayCount() {
  return progress.activity[today()] || 0;
}

/**
 * デイリーミッションの判定。SPIQuest と違い「統計画面を開いた副作用」ではなく
 * 解答直後に明示的に呼ぶ。ボーナスを付与したときだけ bonus > 0 を返す。
 */
export function claimDailyMission() {
  const done = todayCount();
  const goal = DAILY_GOAL;
  if (done < goal || progress.missionClaimed === today()) {
    return { claimed: false, done, goal, bonus: 0 };
  }
  progress.missionClaimed = today();
  const bonus = baseStep() * rankFactor() * 6;
  addEvo(bonus);
  return { claimed: true, done, goal, bonus };
}

/* ── 集計ヘルパ ───────────────────────────────────────── */

export function solvedCount() {
  return Object.keys(progress.solved).length;
}

export function attemptStats(probId) {
  const list = progress.attempts[probId] || [];
  const tries = list.length;
  const ok = list.filter((a) => a.ok).length;
  return { tries, ok, last: list[list.length - 1] || null, best: progress.bestTimes[probId] };
}

/** カテゴリの習熟度 { solved, total, ratio } */
export function categoryProgress(categoryId) {
  const list = CURRICULUM.filter((p) => p.category === categoryId);
  const solved = list.filter((p) => progress.solved[p.id]).length;
  return { solved, total: list.length, ratio: list.length ? solved / list.length : 0 };
}

/** トラックの習熟度 */
export function trackProgress(trackId) {
  const list = CURRICULUM.filter((p) => p.track === trackId);
  const solved = list.filter((p) => progress.solved[p.id]).length;
  return { solved, total: list.length, ratio: list.length ? solved / list.length : 0 };
}

/** 正答率が低い順のカテゴリ（未着手を優先。おすすめ表示用） */
export function weakCategories(n = 3) {
  return CATEGORIES.map((c) => ({ ...c, ...categoryProgress(c.id) }))
    .filter((c) => c.total > 0 && c.solved < c.total)
    .sort((a, b) => a.ratio - b.ratio)
    .slice(0, n);
}

/**
 * 次に解く問題を1問選ぶ。実力レートの ±80 の帯を優先し、
 * 該当がなければ少しずつ条件をゆるめる。
 */
export function pickRandomProblem(trackId) {
  const scope = trackId ? CURRICULUM.filter((p) => p.track === trackId) : CURRICULUM;
  const unsolved = scope.filter((p) => !progress.solved[p.id]);
  if (unsolved.length === 0) return null;
  const rating = myRating();
  let pool = unsolved.filter((p) => Math.abs(p.diff - rating) <= 80);
  if (pool.length === 0) pool = unsolved.filter((p) => p.diff <= rating + 120);
  if (pool.length === 0) pool = unsolved;
  return pool[Math.floor(Math.random() * pool.length)].id;
}

/** Fisher–Yates（配列を破壊しない） */
export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ── バッジ ───────────────────────────────────────────── */

export const BADGES = [
  { id: "first", em: "🌱", name: "はじめの一歩", desc: "最初の1問に正解した", test: () => solvedCount() >= 1 },
  { id: "ten", em: "🔟", name: "十問十答", desc: "10問正解した", test: () => solvedCount() >= 10 },
  { id: "fifty", em: "🏅", name: "半世紀", desc: "50問正解した", test: () => solvedCount() >= 50 },
  { id: "hundred", em: "💯", name: "百問踏破", desc: "100問正解した", test: () => solvedCount() >= 100 },
  {
    id: "allcat",
    em: "🗂️",
    name: "全分野に着手",
    desc: "10カテゴリすべてで1問以上正解した",
    test: () => CATEGORIES.every((c) => categoryProgress(c.id).solved >= 1),
  },
  ...TRACKS.map((t) => ({
    id: "track_" + t.id,
    em: t.em,
    name: `${t.name} 制覇`,
    desc: `${t.name} の全問題に正解した`,
    test: () => {
      const p = trackProgress(t.id);
      return p.total > 0 && p.solved === p.total;
    },
  })),
  {
    id: "hard10",
    em: "🧠",
    name: "応用に強い",
    desc: "応用レベル（難易度270以上）を10問正解した",
    test: () => CURRICULUM.filter((p) => progress.solved[p.id] && p.diff >= 270).length >= 10,
  },
  { id: "combo10", em: "⚡", name: "10連続正解", desc: "コンボを10まで伸ばした", test: () => progress.bestCombo >= 10 },
  { id: "streak7", em: "🔥", name: "一週間の継続", desc: "7日連続で学習した", test: () => progress.bestStreak >= 7 },
  { id: "era_cambrian", em: "🦐", name: "カンブリア到達", desc: "旅がカンブリア紀に到達した", test: () => eraIndexReached("cambrian") },
  { id: "era_devonian", em: "🐟", name: "上陸", desc: "旅がデボン紀に到達した", test: () => eraIndexReached("devonian") },
  { id: "era_cretaceous", em: "🦖", name: "白亜紀到達", desc: "旅が白亜紀に到達した", test: () => eraIndexReached("cretaceous") },
  { id: "era_now", em: "🧑", name: "現在に追いつく", desc: "旅が第四紀に到達した", test: () => eraIndexReached("quaternary") },
  { id: "era_future", em: "🧬", name: "その先へ", desc: "旅が未来ステージに到達した", test: () => eraIndexReached("future") },
];

/** その時代まで到達しているか（時代 id で指定） */
function eraIndexReached(eraId) {
  const target = ERAS.findIndex((e) => e.id === eraId);
  return target >= 0 && eraForEvo(progress.evo).index >= target;
}

/** 新たに獲得したバッジを返す（progress.badges に追記済み） */
export function checkNewBadges() {
  const gained = [];
  for (const b of BADGES) {
    if (progress.badges.includes(b.id)) continue;
    let ok = false;
    try {
      ok = !!b.test();
    } catch (e) {
      ok = false;
    }
    if (ok) {
      progress.badges.push(b.id);
      gained.push(b);
    }
  }
  if (gained.length) save();
  return gained;
}
